/* ─── JHN Suite — tokens, reset, layout, typographie ─────────────────────────
   Palette JHN : encre #0a0a0a · crème #f5f2ec · corail #ef795b
   Esthétique : minimalisme Apple + « liquid glass ».
----------------------------------------------------------------------------- */

:root {
  --coral: #ef795b;
  --coral-600: #e35d3c;
  --coral-soft: rgba(239, 121, 91, 0.16);
  --ink: #0a0a0a;
  --cream: #f5f2ec;

  --font: -apple-system, BlinkMacSystemFont, "SF Pro Display", "SF Pro Text",
    "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
  --font-serif: Georgia, "Times New Roman", serif;
  --mono: ui-monospace, "SF Mono", Menlo, Consolas, monospace;

  --r-sm: 10px;
  --r-md: 16px;
  --r-lg: 22px;
  --r-xl: 30px;

  --sb-w: 264px;
  --topbar-h: 60px;

  --ease: cubic-bezier(0.22, 1, 0.36, 1);
  --dur: 0.32s;
}

/* ── Thème sombre (défaut) ── */
:root,
[data-theme="dark"] {
  color-scheme: dark;
  --bg: #0a0a0a;
  --bg-2: #121214;
  --text: #f5f2ec;
  --text-dim: #a8a39b;
  --text-faint: #6e6a63;

  --glass: rgba(255, 255, 255, 0.055);
  --glass-strong: rgba(255, 255, 255, 0.09);
  --glass-hover: rgba(255, 255, 255, 0.11);
  --border: rgba(255, 255, 255, 0.1);
  --border-strong: rgba(255, 255, 255, 0.16);
  --hairline: rgba(255, 255, 255, 0.06);

  --shadow: 0 20px 50px -12px rgba(0, 0, 0, 0.65);
  --shadow-sm: 0 6px 20px -8px rgba(0, 0, 0, 0.6);
  --glow-1: rgba(239, 121, 91, 0.22);
  --glow-2: rgba(120, 130, 255, 0.14);
}

/* ── Thème clair ── */
[data-theme="light"] {
  color-scheme: light;
  --bg: #f3efe8;
  --bg-2: #ffffff;
  --text: #16140f;
  --text-dim: #6b665d;
  --text-faint: #9b958a;

  --glass: rgba(255, 255, 255, 0.62);
  --glass-strong: rgba(255, 255, 255, 0.78);
  --glass-hover: rgba(255, 255, 255, 0.9);
  --border: rgba(20, 16, 10, 0.08);
  --border-strong: rgba(20, 16, 10, 0.14);
  --hairline: rgba(20, 16, 10, 0.05);

  --shadow: 0 20px 50px -16px rgba(60, 50, 40, 0.28);
  --shadow-sm: 0 8px 22px -12px rgba(60, 50, 40, 0.22);
  --glow-1: rgba(239, 121, 91, 0.2);
  --glow-2: rgba(120, 130, 255, 0.12);
}

* { box-sizing: border-box; }
html, body { height: 100%; }

body {
  margin: 0;
  font-family: var(--font);
  background: var(--bg);
  color: var(--text);
  -webkit-font-smoothing: antialiased;
  text-rendering: optimizeLegibility;
  overflow: hidden;
}

/* Fond ambiant « liquid » : halos doux corail/bleu derrière le verre. */
body::before {
  content: "";
  position: fixed;
  inset: 0;
  z-index: -1;
  background:
    radial-gradient(60% 50% at 82% 8%, var(--glow-1), transparent 60%),
    radial-gradient(50% 45% at 12% 92%, var(--glow-2), transparent 60%),
    radial-gradient(40% 40% at 50% 50%, rgba(255,255,255,0.02), transparent 70%);
  pointer-events: none;
}

a { color: inherit; text-decoration: none; }
button { font-family: inherit; }
img { display: block; }

::selection { background: var(--coral-soft); }

/* Scrollbars discrètes */
*::-webkit-scrollbar { width: 10px; height: 10px; }
*::-webkit-scrollbar-thumb {
  background: var(--border-strong);
  border-radius: 99px;
  border: 3px solid transparent;
  background-clip: padding-box;
}
*::-webkit-scrollbar-thumb:hover { background: var(--text-faint); background-clip: padding-box; }

h1, h2, h3 { letter-spacing: -0.02em; font-weight: 650; margin: 0; }

/* ── Écran de boot ── */
.boot {
  position: fixed; inset: 0; display: grid; place-content: center; gap: 22px; justify-items: center;
}
.boot img { border-radius: 14px; box-shadow: var(--shadow-sm); animation: floaty 3s var(--ease) infinite; }
.boot-spin {
  width: 26px; height: 26px; border-radius: 50%;
  border: 2.5px solid var(--border-strong); border-top-color: var(--coral);
  animation: spin 0.8s linear infinite;
}
@keyframes spin { to { transform: rotate(360deg); } }
@keyframes floaty { 50% { transform: translateY(-6px); } }

/* ── Layout principal (shell) ── */
.shell { display: grid; grid-template-columns: var(--sb-w) 1fr; height: 100vh; height: 100dvh; }
.shell.collapsed { grid-template-columns: 76px 1fr; }
.main {
  min-width: 0; display: grid; grid-template-rows: var(--topbar-h) 1fr;
  height: 100vh; height: 100dvh;
}
.view { overflow: auto; padding: 28px 34px 60px; }

@media (max-width: 820px) {
  .shell, .shell.collapsed { grid-template-columns: 1fr; }
  /* Mobile : la sidebar redevient un overlay glissant AVEC fond (lisible). */
  .shell .sidebar {
    position: fixed; z-index: 60; top: 0; bottom: 0; left: 0; right: auto;
    width: 280px; max-width: 84vw; margin: 0; border-radius: 0; box-shadow: none;
    transform: translateX(-110%); transition: transform 0.3s var(--ease);
    padding-top: calc(16px + env(safe-area-inset-top));
    background: var(--glass-strong); border-right: 1px solid var(--border);
    -webkit-backdrop-filter: blur(26px) saturate(180%); backdrop-filter: blur(26px) saturate(180%);
  }
  .shell .sidebar.open { transform: none; box-shadow: var(--shadow); }
  .view { padding: 18px 16px 60px; }
  /* Topbar mobile : on garde la photo de profil visible à droite. */
  .topbar { padding: 0 12px 0 6px; gap: 8px; }
  .topbar .crumbs { display: none; }
  .topbar .search { min-width: 0; }
}

.fade-in { animation: fadeIn var(--dur) var(--ease); }
@keyframes fadeIn { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: none; } }

.sr-only {
  position: absolute; width: 1px; height: 1px; padding: 0; margin: -1px;
  overflow: hidden; clip: rect(0,0,0,0); white-space: nowrap; border: 0;
}

/* ── Mode app installée (PWA iPhone) : respect des zones de sécurité ── */
@media (display-mode: standalone) {
  .main { padding-top: env(safe-area-inset-top); }
  .sidebar { padding-top: calc(16px + env(safe-area-inset-top)); }
  .msg-composer { padding-bottom: calc(12px + env(safe-area-inset-bottom)); }
  .gdoc-header { padding-top: calc(8px + env(safe-area-inset-top)); }
  .standalone .gdoc-canvas { padding-bottom: calc(80px + env(safe-area-inset-bottom)); }
}
